<script setup lang="ts">
import CatalogPagination from '~/components/products/CatalogPagination.vue';
import CategoryDescription from '~/components/products/CategoryDescription.vue';
import PortfolioGallery from '~/components/gallery/PortfolioGallery.vue';
import GalleryPageHeader from '~/components/gallery/GalleryPageHeader.vue';
import { catalogCanonicalPath, catalogPage } from '~/utils/catalogPagination';
import type { GalleryAlbumPage } from '~/utils/gallery';

const { t, locale } = useI18n();
const route = useRoute();
const { $api } = useNuxtApp();
const page = computed(() => catalogPage(route.query.page));
const { data, error, status } = await useAsyncData(
  computed(() => `gallery-album:${route.params.slug}:${locale.value}:${page.value}`),
  () => $api<GalleryAlbumPage>('gallery/' + encodeURIComponent(String(route.params.slug)), { query: { page: page.value, per_page: 48 } }),
);
if (error.value) throw createError(error.value);
watch(error, value => { if (value) showError(value); });
const album = computed(() => data.value?.album);
usePageSeo(computed(() => ({
  name: album.value?.name || t('gallery_module.title'), short_description: album.value?.short_description,
  photo: album.value?.cover?.photo || null, icon: album.value?.cover?.icon || null, schemaType: 'ImageGallery',
  canonicalPath: catalogCanonicalPath(route.path, route.query.page), modifiedAt: album.value?.updated_at,
})));
useHead(() => ({
  script: [{
    key: 'gallery-images', type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org', '@type': 'ItemList', name: album.value?.name,
      numberOfItems: album.value?.photos_count,
      itemListElement: (data.value?.photos || []).map((photo, index) => ({
        '@type': 'ListItem', position: (page.value - 1) * 48 + index + 1,
        item: { '@type': 'ImageObject', contentUrl: photo.photo, thumbnailUrl: photo.thumb || photo.icon,
          name: photo.name || album.value?.name, width: photo.width || undefined, height: photo.height || undefined },
      })),
    }).replace(/</g, '\u003c'),
  }],
}));
</script>

<template>
  <section v-if="album" class="container-xxl gallery-album-page" :aria-busy="status === 'pending'">
    <GalleryPageHeader :title="album.name" />
    <PortfolioGallery v-if="data?.photos.length" :photos="data.photos" :name="album.name" :offset="(page - 1) * 48" />
    <el-empty v-else :description="t('gallery_module.empty_photos')" />
    <CatalogPagination v-if="data?.meta" :current-page="data.meta.current_page" :last-page="data.meta.last_page" />
    <div v-if="album.description" class="gallery-description"><CategoryDescription :description="album.description" /></div>
  </section>
</template>

<style scoped>
.gallery-description { margin: 3rem auto 0; max-width: 60rem; }
</style>
